/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import _ from 'lodash';
import Konva from 'konva';
import Layer = Konva.Layer;
import Stage = Konva.Stage;
import Rect = Konva.Rect;
import Text = Konva.Text;
import Group = Konva.Group;
// ours
import ProjectJaeger from '../ProjectJaeger';
import { FullUserModel } from 'src/types/user.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import ProjectJaegerProps from '../data/project-jaeger.props';
import ProjectJaegerState from '../data/project-jaeger.state';
import TLTimeline from '../elements/TLTimeline';
import TLPinboard from '../elements/TLPinboard';

export default class TimelineService {
    private static instance: TimelineService;
    private component: ProjectJaeger;
    public readonly TRASH_WIDTH = 185;
    public readonly TRASH_HEIGHT = 80;
    public pinBoard: TLPinboard;

    // prevent new with private constructor
    private constructor(component: ProjectJaeger) {
        // creates pointers to these items in memory, so we always have the latest props and state
        this.component = component;
    }

    private getFromComponent<Type>(item: string): Type {
        try {
            return _.get(this.component, item);
        } catch (e: unknown) {
            TimelineService.reportError(e);
        }
    }

    private static reportError(e?: unknown): void {
        console.error('TimelineService likely improperly initialised', e);
    }

    private executeFromComponent<Type>(item: string, args?: Type): Type {
        try {
            return _.get(this.component, item)(args);
        } catch (e: unknown) {
            TimelineService.reportError(e);
        }
    }

    public getCashFlow(user?: FullUserModel): CashFlowModel[] {
        user = user || this.component.props.user;
        return this.component.state.zoom === 'months' ? user.rawCashFlow : user.cashFlow;
    }

    public static getInstance(component?: ProjectJaeger): TimelineService {
        if (!TimelineService.instance) {
            component ? (TimelineService.instance = new TimelineService(component)) : this.reportError();
        }

        return TimelineService.instance;
    }

    public isOverTrash(x: number, y: number): boolean {
        const stage: Stage = this.getStage();
        const stageX: number = stage.x();
        const trash: Group = this.getTrash();
        const trashX: number = trash?.x();
        const trashY: number = trash?.y();

        return (
            x + stageX > trashX &&
            x + stageX < trashX + this.TRASH_WIDTH &&
            y > trashY &&
            y < trashY + this.TRASH_HEIGHT
        );
    }

    public updateComponentIfNecessary(component: ProjectJaeger): void {
        if (!_.isEqual(this.component.props.user, component.props.user)) this.component = new Proxy(component, {});
    }

    public getBlockSnapSize(): number {
        return this.getFromComponent<number>('blockSnapSize');
    }

    public getMainLayer(): Layer {
        return this.getFromComponent<Layer>('mainLayer');
    }

    public getProps(): ProjectJaegerProps {
        return this.getFromComponent<ProjectJaegerProps>('props');
    }

    public getStage(): Stage {
        return this.getFromComponent<Stage>('stage');
    }

    public getState(): ProjectJaegerState {
        return this.getFromComponent<ProjectJaegerState>('state');
    }

    public getTimeline(): TLTimeline {
        return this.getFromComponent<TLTimeline>('timeline');
    }

    public getTrash(): Group {
        return this.getFromComponent<Group>('trash');
    }

    public getTrashRect(): Rect {
        return this.getFromComponent<Rect>('trashRect');
    }

    public getTrashText(): Text {
        return this.getFromComponent<Text>('trashText');
    }

    public onChangeIcon(): void {
        if (this.getStage()) {
            this.executeFromComponent<() => void>('clearEventImages');
            this.pinBoard.updatePins(this.getProps()?.user?.cards);
        }
    }

    public setState(args: any): void {
        this.component.setState(args);
    }

    public setupPins(): void {
        if (this.component?.props?.user) {
            if (this.pinBoard) {
                this.pinBoard.upsert();
            } else {
                this.pinBoard = new TLPinboard(this.component);
                this.pinBoard.create();
            }
        }
    }

    public setZoom(magnification: number): void {
        const zoom: string = this.getState().zoom;

        switch (true) {
            case zoom !== 'months' && magnification > 10:
                this.setState({
                    zoom: 'months'
                });
                break;
            case zoom !== 'years' && magnification < 10:
                this.setState({
                    zoom: 'years'
                });
                break;
        }
    }
}
